import { Alert, Box, Button, Stack, Typography, Link } from '@mui/material'
import React, { useMemo } from 'react'
import { Link as RouterLink, useNavigate } from 'react-router-dom'

import { CodeBlock, CodeWrapper } from 'components/common/CodeBlock'
import ExternalLink from 'components/common/ExternalLink'
import { TutorialStep } from 'components/common/TutorialStep'
import Icon from 'components/Icon'
import { trans } from 'i18n/translate'
import theme from 'theme'

interface AlertDetails {
  level: 'warning' | 'info' | 'success'
  message: string
  url?: string
  urlText?: string
}

export const PrepareToDefineServicesTutorial: React.FC<{
  numberOfActiveClients: number
  numberOfHealthyClients: number
}> = ({ numberOfActiveClients, numberOfHealthyClients }) => {
  const navigate = useNavigate()

  const activeClientsEqualHealthyClients =
    numberOfActiveClients > 0 &&
    numberOfActiveClients === numberOfHealthyClients

  const alertDetails: AlertDetails = useMemo(() => {
    if (numberOfActiveClients === 0) {
      return {
        level: 'info',
        message: ' Gremlin is waiting for agents to be installed.'
      }
    }

    if (activeClientsEqualHealthyClients) {
      return {
        level: 'success',
        message:
          'Gremlin is seeing full communication for all agents installed for this team.'
      }
    }

    return {
      level: 'warning',
      message: `Gremlin is not getting full communication from ${
        numberOfActiveClients - numberOfHealthyClients
      }/${numberOfActiveClients} agents. Visit `,
      url: '/clients/hosts',
      urlText: 'Agents.'
    }
  }, [
    activeClientsEqualHealthyClients,
    numberOfActiveClients,
    numberOfHealthyClients
  ])

  return (
    <>
      <Typography variant="body1">
        {trans('getting_started.prepare_to_define_services_tutorial.text')}
      </Typography>
      <TutorialStep
        badgeNumber={1}
        title={trans(
          'getting_started.prepare_to_define_services_tutorial.install_gremlin.title'
        )}
      >
        <Stack spacing={2}>
          <Box>
            {trans(
              'getting_started.prepare_to_define_services_tutorial.install_gremlin.paragraph1'
            )}{' '}
            <ExternalLink href="https://www.gremlin.com/docs/reliability-management/quick-start-guide">
              {trans(
                'getting_started.prepare_to_define_services_tutorial.install_gremlin.paragraph2'
              )}
              <Icon
                css={{
                  fontSize: theme.fontSizes[2],
                  marginLeft: theme.space[1]
                }}
                icon="arrowUpRightFromSquare"
              />
            </ExternalLink>{' '}
            {trans(
              'getting_started.prepare_to_define_services_tutorial.install_gremlin.paragraph3'
            )}
          </Box>
        </Stack>
      </TutorialStep>
      <TutorialStep
        badgeNumber={2}
        title={trans(
          'getting_started.prepare_to_define_services_tutorial.verify_gremlin.title'
        )}
      >
        <Stack spacing={2}>
          <Typography>
            {trans(
              'getting_started.prepare_to_define_services_tutorial.verify_gremlin.paragraph'
            )}
          </Typography>
          <Alert
            data-test-getting-started-agent-alert
            severity={alertDetails.level}
          >
            {alertDetails.message}
            {alertDetails.url && (
              <Link component={RouterLink} to={alertDetails.url}>
                {alertDetails.urlText}
              </Link>
            )}
          </Alert>
        </Stack>
      </TutorialStep>
      <TutorialStep
        badgeNumber={3}
        title={trans(
          'getting_started.prepare_to_define_services_tutorial.define_your_service.title'
        )}
      >
        {trans(
          'getting_started.prepare_to_define_services_tutorial.define_your_service.paragraph1'
        )}{' '}
        <ExternalLink href="https://www.gremlin.com/docs/reliability-management/services/#adding-a-service-by-using-an-annotation">
          {trans(
            'getting_started.prepare_to_define_services_tutorial.define_your_service.paragraph2'
          )}
          <Icon
            css={{
              fontSize: theme.fontSizes[2],
              marginLeft: theme.space[1]
            }}
            icon="arrowUpRightFromSquare"
          />
        </ExternalLink>
        .
        <Stack direction="column" mt={2} spacing={2}>
          <Button
            color="secondary"
            css={{
              width: 'fit-content'
            }}
            startIcon={<Icon icon="plus" />}
            variant="contained"
            onClick={() => navigate('/services/new')}
          >
            {trans(
              'getting_started.prepare_to_define_services_tutorial.define_your_service.button.text'
            )}
          </Button>
          <Typography>
            {trans(
              'getting_started.prepare_to_define_services_tutorial.define_your_service.paragraph3'
            )}
          </Typography>
          <CodeWrapper show>
            <div>
              <CodeBlock
                code={
                  'annotations: \n' +
                  '     gremlin.com/service-id: my-nginx-service'
                }
                language="yaml"
              />
            </div>
          </CodeWrapper>
        </Stack>
      </TutorialStep>
      <TutorialStep
        badgeNumber={4}
        title={trans(
          'getting_started.prepare_to_define_services_tutorial.define_status_check.title'
        )}
      >
        {trans(
          'getting_started.prepare_to_define_services_tutorial.define_status_check.paragraph'
        )}
      </TutorialStep>
      <TutorialStep
        badgeNumber={5}
        title={trans(
          'getting_started.prepare_to_define_services_tutorial.run_a_reliability_test.title'
        )}
      >
        {trans(
          'getting_started.prepare_to_define_services_tutorial.run_a_reliability_test.paragraph1'
        )}{' '}
        <Link color="secondary" component={RouterLink} to="/test-suites">
          {trans(
            'getting_started.prepare_to_define_services_tutorial.run_a_reliability_test.paragraph2'
          )}
        </Link>{' '}
        {trans(
          'getting_started.prepare_to_define_services_tutorial.run_a_reliability_test.paragraph3'
        )}
      </TutorialStep>
    </>
  )
}
